import OktaAuth, {
  prepareTokenParams,
  getOAuthUrls,
  buildAuthorizeParams,
  addPostMessageListener,
  loadFrame,
  OAuthResponse
} from '@okta/okta-auth-js';

import {getRoute} from 'config/navigation';
import {StaticConfig} from 'config/staticConfig';
import oktaClient from 'shared/utils/okta/okta-client';

interface GenesysAuthData {
  authCode?: string;
  redirectUri?: string;
  nonce?: string;
  codeVerifier?: string;
}

type GenesysPromiseResolver = {resolve: (data?: GenesysAuthData) => void; reject: () => void};

declare let Genesys: any;

const getCodeFromOkta = (e: GenesysPromiseResolver) => {
  const oktaClientForAuthenticatedMessenger = new OktaAuth({
    issuer: StaticConfig.oktaIssuer,
    redirectUri: `${window.location.origin}/customers${getRoute('/messenger/callback')}`,
    clientId: StaticConfig.oktaClientIdAuthMessenger,
    scopes: ['openid', 'profile', 'email', 'offline_access']
  });

  prepareTokenParams(oktaClientForAuthenticatedMessenger, {responseMode: 'okta_post_message'}).then((params) => {
    const urls = getOAuthUrls(oktaClientForAuthenticatedMessenger, params);
    const endpoint = urls.authorizeUrl;
    const authParams = buildAuthorizeParams(params);
    const requestUrl = endpoint + authParams;
    const iframePromise = addPostMessageListener(oktaClientForAuthenticatedMessenger, undefined, params.state);
    const iframeEl = loadFrame(requestUrl);

    iframePromise
      .then((res: OAuthResponse) => {
        const authData = {
          authCode: res.code, // Pass your authCode here
          redirectUri: params.redirectUri, // Pass the redirection URI configured in your authentication provider here
          nonce: params.nonce, //  Mandatory parameter in OKTA Javascript SDK approach.
          codeVerifier: params.codeVerifier // pass your code verifier here when PKCE flow is enabled
        };
        (window as any).GenesysAuthData = authData;
        e.resolve(authData);
      })
      .catch(() => {
        //eslint-disable-next-line
        console.error('Error getting auth code from Okta for Genesys authentication');
        e.reject();
      })
      .finally(() => {
        if (document.body.contains(iframeEl)) {
          iframeEl.parentElement?.removeChild(iframeEl);
        }
      });
  });
};

export const registerGenesysAuthenticationProvider = () => {
  clearGenesysAuthLocalStorageKeys();
  Genesys('subscribe', 'MessagingService.started', () => {
    oktaClient.isAuthenticated().then((isAuthenticated) => {
      if (isAuthenticated) {
        Genesys('command', 'Auth.signIn');
      }
    });
  });

  Genesys('registerPlugin', 'AuthProvider', (AuthProvider: any) => {
    AuthProvider.registerCommand('signIn', (e: GenesysPromiseResolver) => {
      oktaClient.isAuthenticated().then((isAuthenticated) => {
        if (isAuthenticated) {
          getCodeFromOkta(e);
        } else {
          e.resolve();
        }
      });
    });

    AuthProvider.registerCommand('getAuthCode', (e: GenesysPromiseResolver) => {
      const data = {...(window as any).GenesysAuthData};

      if (data) {
        (window as any).GenesysAuthData = null;
        e.resolve(data);
      } else {
        getCodeFromOkta(e);
      }
    });

    setTimeout(() => {
      AuthProvider.ready();
    }, 1000);
  });
};

export const clearGenesysAuthLocalStorageKeys = () => {
  const AUTH_STORAGE_KEY = 'gcatkn';
  const REFRESH_STORAGE_KEY = 'gcartk';
  const keysToRemove = Object.keys(localStorage).filter(
    (key) => key.includes(AUTH_STORAGE_KEY) || key.includes(REFRESH_STORAGE_KEY)
  );

  Object.keys(localStorage).forEach((key) => {
    if (keysToRemove.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};
